import { Card, Stack, Typography } from '@mui/material';
import type {
  BackupPolicyType,
  Condition,
  ResourceType,
} from '@repo/api-gw-sdk';

import { ConditionViewer } from '@/components/conditions/ConditionViewer';
import {
  isAnAllCondition,
  isEmptyCondition,
  type FilterProperty,
} from '@/types/advanceFilter';

import {
  DynamicResourcesAllExcluded,
  DynamicResourcesAllIncluded,
} from '../../app/(dashboard)/settings/backup-policies/backup-scopes/[tab]/dynamicResourcesEmptyState';

export type ResourcesScopeType =
  | 'allIncluded'
  | 'allExcluded'
  | 'useConditions';

export const getResourcesScopeType = (
  condition: Condition | undefined,
  fallback: ResourcesScopeType
): ResourcesScopeType => {
  if (condition && isAnAllCondition(condition)) {
    if (condition.value?.[0] === 'true') {
      return 'allIncluded';
    }
    if (condition.value?.[0] === 'false') {
      return 'allExcluded';
    }
  }

  if (!condition || isEmptyCondition(condition)) {
    return fallback;
  }
  return 'useConditions';
};

interface ResourceSelectionDynamicViewerProps {
  condition?: Condition;
  conditionTitle: React.ReactNode;
  resourcesScopeType: ResourcesScopeType;
  allExcludedDescription: string;
  allIncludedDescription: string;
  supportProperties: FilterProperty[];
  selectedResourceTypes?: ResourceType[];
  policyType?: BackupPolicyType;
}

export const ResourceSelectionDynamicViewer = ({
  condition,
  conditionTitle,
  resourcesScopeType,
  allExcludedDescription,
  allIncludedDescription,
  supportProperties,
  selectedResourceTypes,
  policyType,
}: ResourceSelectionDynamicViewerProps) => {
  return (
    <Card
      sx={{
        width: 'auto',
        border: '1px solid var(--mui-palette-divider)',
        padding: '24px',
        backgroundColor: 'transparent',
      }}
    >
      {resourcesScopeType === 'useConditions' && condition ? (
        <Stack gap='16px'>
          {typeof conditionTitle === 'string' ? (
            <Typography variant='subtitle1'>{conditionTitle}</Typography>
          ) : (
            conditionTitle
          )}
          <ConditionViewer
            condition={condition}
            supportProperties={supportProperties}
          />
        </Stack>
      ) : (
        <>
          {resourcesScopeType === 'allExcluded' ? (
            <DynamicResourcesAllExcluded
              description={allExcludedDescription}
              size='small'
            />
          ) : (
            <DynamicResourcesAllIncluded
              description={allIncludedDescription}
              size='small'
              selectedResourceTypes={selectedResourceTypes}
              policyType={policyType}
            />
          )}
        </>
      )}
    </Card>
  );
};
